import {
  CIRCULAR_VALUE,
  isSensitiveObservabilityKey,
  isUrlBearingObservabilityKey,
  REDACTED_VALUE,
} from "./redaction-rules";
import { sanitizeObservabilityText } from "./redaction-text";
import { sanitizeObservabilityUrl } from "./redaction-url";

const MAX_REDACTION_DEPTH = 8;

function redactString(key: string, value: string): string {
  return isUrlBearingObservabilityKey(key)
    ? sanitizeObservabilityUrl(value)
    : sanitizeObservabilityText(value);
}

function redactEntry(key: string, value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (isSensitiveObservabilityKey(key)) {
    return REDACTED_VALUE;
  }
  if (typeof value === "string") {
    return redactString(key, value);
  }
  return redactValue(value, seen, depth + 1);
}

function redactRecord(
  value: Record<string, unknown>,
  seen: WeakSet<object>,
  depth: number,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, redactEntry(key, child, seen, depth)]),
  );
}

function redactValue(value: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (value === null || typeof value !== "object") {
    return typeof value === "string" ? sanitizeObservabilityText(value) : value;
  }
  if (depth >= MAX_REDACTION_DEPTH) {
    return REDACTED_VALUE;
  }
  if (seen.has(value)) {
    return CIRCULAR_VALUE;
  }

  seen.add(value);
  return Array.isArray(value)
    ? value.map((child) => redactValue(child, seen, depth + 1))
    : redactRecord(value as Record<string, unknown>, seen, depth);
}

/** Deeply redacts sensitive object keys while tolerating cycles and nesting. */
export function redactSensitiveKeys(value: unknown): unknown {
  return redactValue(value, new WeakSet(), 0);
}
